import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';
import moment from 'moment';
import { AppDispatch, CombinedStates } from 'core/types';
import { SECURITY_USER } from 'core/utils/value';
import { fetchUnreadNotifications, fetchRecentNotifications } from './notificationApi';
import { Notification } from './types';

const POLLING_INTERVAL = 30000; // 30 seconds
// Only toast notifications created within this window, so loading the bell's
// full unread list on mount/reload doesn't re-toast old, still-unread exports.
const TOAST_FRESHNESS_MINUTES = 5;

/**
 * An export-ready notification carries an authenticated download URL (set by the
 * backend export jobs, e.g. ExportIncidentsCsvJob). We toast only these — not
 * navigation/approval notifications — so the user is told the moment a file
 * (e.g. the incident CSV) is ready, instead of only seeing the bell dot.
 */
const isDownloadReady = (n: Notification): boolean => !!n.actionUrl && !!n.authenticated;

const NotificationPoller: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { userData } = useSelector((state: CombinedStates) => state.user);
    const notifications = useSelector((state: CombinedStates) => state.notification.notifications);
    const isAuthenticated = !!userData?.id;
    const isSecurityUser = userData?.role?.accessLevel === SECURITY_USER;

    // IDs already toasted this session, so each notification toasts at most once
    // (it lingers in the store across polls, and the bell re-fetches it).
    const toastedIdsRef = useRef<Set<string>>(new Set());

    // Fetch loop — unchanged: unread on mount (populates the bell), recent every 30s.
    useEffect(() => {
        if (!isAuthenticated || isSecurityUser) return undefined;

        dispatch(fetchUnreadNotifications());

        const interval = setInterval(() => {
            dispatch(fetchRecentNotifications());
        }, POLLING_INTERVAL);

        return () => clearInterval(interval);
    }, [isAuthenticated, isSecurityUser, dispatch]);

    // Toast freshly-arrived, download-ready notifications as they land in the
    // store, so the user gets a visible message — not just the bell dot. Driven
    // off the store (not the fetch promise) so it fires whether the notification
    // arrived via the unread fetch or a recent poll.
    useEffect(() => {
        if (!isAuthenticated || isSecurityUser) return;

        notifications.forEach((n) => {
            if (toastedIdsRef.current.has(n.id) || n.readAt || !isDownloadReady(n)) return;
            if (moment().diff(moment(n.createdAt), 'minutes') > TOAST_FRESHNESS_MINUTES) return;

            toastedIdsRef.current.add(n.id);
            notification.success({
                key: n.id,
                message: n.title,
                description: n.message,
                duration: 0, // sticky — the user may be on another page when it lands
            });
        });
    }, [notifications, isAuthenticated, isSecurityUser]);

    return null;
};

export default NotificationPoller;
